import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, ChevronDown, ChevronLeft, ChevronRight, Home, Eye } from 'lucide-react';
import { questionAPI } from '../services/api';
import MathRenderer from '../components/math/MathRenderer';
import WorkedSolutionPanel from '../components/questions/WorkedSolutionPanel';
import SimilarQuestionPanel from '../components/questions/SimilarQuestionPanel';
import {
  QuestionApiModel,
  QuestionOption,
  ShortAnswerQuestionGroup,
  ShortAnswerSubQuestion,
  isShortAnswerQuestionGroup,
} from '../types/questions';
import { groupShortAnswerQuestions, isWrittenResponseType } from '../utils/questions';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';

type LibraryQuestion = QuestionApiModel | ShortAnswerQuestionGroup;
type ExamFilter = 'all' | 'exam1' | 'exam2-mc' | 'exam2-er';

const EXAM_FILTER_OPTIONS: { value: ExamFilter; label: string; description: string }[] = [
  { value: 'all', label: 'All Exams', description: 'Exam 1 + Exam 2' },
  { value: 'exam1', label: 'Exam 1', description: 'Short answer' },
  { value: 'exam2-mc', label: 'Exam 2 Multiple Choice', description: 'MC only' },
  { value: 'exam2-er', label: 'Exam 2 Extended Response', description: 'ER only' },
];

const buildQuestionKey = (question: LibraryQuestion): string => {
  if (isShortAnswerQuestionGroup(question)) {
    return question.id;
  }
  return question.id;
};

const getQuestionTypeLabel = (questionType?: string) => {
  if (questionType === 'SA') return 'Short Answer';
  if (questionType === 'ER') return 'Extended Response';
  return 'Multiple Choice';
};

const formatProgressDate = (value?: string | null) => {
  if (!value) return '';
  try {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    return parsed.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

const getQuestionStatus = (question?: LibraryQuestion | null) => {
  const progress = question?.progress;
  if (!progress || !progress.last_attempt_at || progress.last_attempt_was_correct === null || progress.last_attempt_was_correct === undefined) {
    return {
      label: 'Unseen',
      className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
    };
  }

  const formatted = formatProgressDate(progress.last_attempt_at);
  if (progress.last_attempt_was_correct) {
    return {
      label: formatted ? `Last Solved Correctly · ${formatted}` : 'Last Solved Correctly',
      className: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
    };
  }

  return {
    label: formatted ? `Last Solved Incorrectly · ${formatted}` : 'Last Solved Incorrectly',
    className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  };
};

const getDifficultyColor = (rating: number | null) => {
  if (rating === null || rating === undefined) return 'text-gray-500 dark:text-gray-400';
  if (rating < 25) return 'text-green-600 dark:text-green-400';
  if (rating < 35) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
};

const findCorrectOption = (options: QuestionOption[], correctAnswer: string) => {
  return options.find((option) => option.key === correctAnswer) ?? null;
};

const ShortAnswerLibraryCard: React.FC<{
  question: ShortAnswerQuestionGroup;
  canGenerateSimilar: boolean;
}> = ({ question, canGenerateSimilar }) => {
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [selectedOption, setSelectedOption] = useState<Record<string, string | null>>({});
  const typeLabel = getQuestionTypeLabel(question.question_type);
  const similarQuestions = useMemo(
    () =>
      question.sub_questions.map((subQuestion, index) => {
        const label =
          subQuestion.sub_question === 'N/A'
            ? `Part ${String.fromCharCode(65 + index)}`
            : `Part ${subQuestion.sub_question}`;
        return { id: subQuestion.id, label };
      }),
    [question.sub_questions]
  );

  useEffect(() => {
    setRevealed({});
    setSelectedOption({});
  }, [question.id]);

  const progressStatus = getQuestionStatus(question);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {question.year} Exam {question.exam_type} · {typeLabel} Q{question.question_number}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Area of Study: {question.vce_area_of_study}
            {question.total_marks ? ` · Total Marks: ${question.total_marks}` : ''}
          </p>
        </div>
        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${progressStatus.className}`}>
          {progressStatus.label}
        </span>
      </header>

      {question.sub_questions.map((subQuestion: ShortAnswerSubQuestion) => {
        const isRevealed = revealed[subQuestion.id] ?? false;
        const correctOption = findCorrectOption(subQuestion.options, subQuestion.correct_answer);

        return (
          <div
            key={subQuestion.id}
            className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4"
          >
            <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-3">
              <div className="min-w-0 max-w-full">
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  {subQuestion.sub_question === 'N/A' ? 'Part A' : `Part ${subQuestion.sub_question}`}
                  {subQuestion.marks ? ` · ${subQuestion.marks} mark${subQuestion.marks === 1 ? '' : 's'}` : ''}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Difficulty:{' '}
                  <span className={getDifficultyColor(subQuestion.difficulty_rating)}>
                    {subQuestion.difficulty_rating ?? 'N/A'}
                  </span>
                </div>
                <div className="overflow-x-auto text-gray-900 dark:text-white">
                  <MathRenderer latex={subQuestion.question_text} />
                </div>
              </div>
              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                {question.vce_area_of_study}
              </span>
            </div>

            <div className="space-y-2">
              {subQuestion.options.map((option) => {
                const isCorrect = option.key === subQuestion.correct_answer;
                const isSelected = selectedOption[subQuestion.id] === option.key;
                const highlight = isRevealed && isCorrect;
                let optionClasses = 'border-gray-200 dark:border-gray-700';

                if (highlight) {
                  optionClasses = 'border-green-500 bg-green-50 dark:bg-green-900/20';
                } else if (!isRevealed && isSelected) {
                  optionClasses = 'border-subject-500 bg-subject-50 dark:bg-subject-900/20';
                }

                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      if (isRevealed) return;
                      setSelectedOption((prev) => ({
                        ...prev,
                        [subQuestion.id]: option.key,
                      }));
                    }}
                    className={`w-full flex items-start gap-3 p-3 border-2 rounded-lg text-left transition-all ${
                      isRevealed ? 'cursor-default' : 'hover:shadow-sm'
                    } ${optionClasses}`}
                  >
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-sm font-medium text-gray-700 dark:text-gray-200">
                      {option.key}
                    </span>
                    <div className="flex-1 min-w-0 overflow-x-auto text-gray-900 dark:text-white">
                      <MathRenderer latex={option.text} />
                    </div>
                  </button>
                );
              })}
            </div>

            {!isRevealed ? (
              <button
                onClick={() =>
                  setRevealed((prev) => ({
                    ...prev,
                    [subQuestion.id]: true,
                  }))
                }
                className="inline-flex items-center px-4 py-2 bg-subject-600 text-white rounded-lg hover:bg-subject-500 transition-colors text-sm"
              >
                <Eye className="h-4 w-4 mr-2" />
                Reveal Answer
              </button>
            ) : (
              <div className="max-w-full overflow-x-auto px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm text-gray-800 dark:text-gray-200">
                <span className="font-semibold">Correct Answer:</span>{' '}
                {correctOption ? <MathRenderer latex={correctOption.text} /> : 'Not available'}
              </div>
            )}

            {subQuestion.worked_solution ? (
              <WorkedSolutionPanel
                solution={subQuestion.worked_solution}
                isVisible={isRevealed}
              />
            ) : (
              <div className="px-4 py-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg text-sm text-yellow-800 dark:text-yellow-200">
                Worked solution not available yet.
              </div>
            )}
          </div>
        );
      })}

      <SimilarQuestionPanel
        questions={similarQuestions}
        canGenerate={canGenerateSimilar}
      />
    </div>
  );
};

const QuestionLibrary: React.FC = () => {
  const { year: urlYear, questionNumber: urlQuestionNumber } = useParams<{ year?: string; questionNumber?: string }>();
  const navigate = useNavigate();

  const [selectedYear, setSelectedYear] = useState<number | null>(urlYear ? parseInt(urlYear, 10) : null);
  const [currentQuestionNumber, setCurrentQuestionNumber] = useState<number | null>(
    urlQuestionNumber ? parseInt(urlQuestionNumber, 10) : null
  );
  const [examFilter, setExamFilter] = useState<ExamFilter>('all');
  const [currentExamType, setCurrentExamType] = useState<number | null>(null);
  const [currentQuestionType, setCurrentQuestionType] = useState<string | null>(null);
  const [question, setQuestion] = useState<LibraryQuestion | null>(null);
  const [questionsInYear, setQuestionsInYear] = useState<LibraryQuestion[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [yearQuestions, setYearQuestions] = useState<Record<number, LibraryQuestion[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [yearsLoading, setYearsLoading] = useState(true);
  const [answerRevealed, setAnswerRevealed] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [questionsError, setQuestionsError] = useState<string | null>(null);
  const { subscriptionStatus } = useSubscriptionStatus();

  useEffect(() => {
    const loadAvailableYears = async () => {
      try {
        setYearsLoading(true);
        const years = await questionAPI.getAvailableYears();
        setAvailableYears(years);
      } catch (error) {
        console.error('Failed to load available years:', error);
      } finally {
        setYearsLoading(false);
      }
    };

    loadAvailableYears();
  }, []);

  useEffect(() => {
    if (!selectedYear) {
      setQuestionsInYear([]);
      setQuestion(null);
      setCurrentExamType(null);
      setCurrentQuestionType(null);
      setQuestionsError(null);
      return;
    }

    const storedQuestions = yearQuestions[selectedYear];
    if (storedQuestions) {
      setQuestionsInYear(storedQuestions);

      if (currentQuestionNumber !== null) {
        const existingQuestion =
          storedQuestions.find((item) => {
            if (currentExamType !== null && item.exam_type !== currentExamType) {
              return false;
            }
            if (currentQuestionType && item.question_type !== currentQuestionType) {
              return false;
            }
            return item.question_number === currentQuestionNumber;
          }) ??
          storedQuestions.find((item) => item.question_number === currentQuestionNumber) ??
          null;
        setQuestion(existingQuestion);
        if (!existingQuestion) {
          setCurrentQuestionNumber(null);
          setCurrentExamType(null);
          setCurrentQuestionType(null);
          navigate('/library', { replace: true });
        }
      } else {
        setQuestion(null);
      }
      setQuestionsError(null);
      return;
    }

    const loadQuestionsForYear = async () => {
      try {
        setIsLoading(true);
        setQuestionsError(null);
        const pageSize = 100;
        const maxPages = 50;
        const pagedQuestions: QuestionApiModel[] = [];
        let skip = 0;

        for (let page = 0; page < maxPages; page += 1) {
          const response = await questionAPI.getQuestions({
            year: selectedYear,
            limit: pageSize,
            skip,
          });
          const batch: QuestionApiModel[] = Array.isArray(response) ? response : [];

          pagedQuestions.push(...batch);

          if (batch.length < pageSize) {
            break;
          }

          skip += batch.length;
        }

        const apiQuestions = pagedQuestions;
        const mcQuestions = apiQuestions.filter((item) => item.question_type === 'MC');
        const shortAnswerGroups = groupShortAnswerQuestions(
          apiQuestions.filter((item) => isWrittenResponseType(item.question_type))
        );

        const combined: LibraryQuestion[] = [...mcQuestions, ...shortAnswerGroups].sort((a, b) => {
          if (a.exam_type !== b.exam_type) return a.exam_type - b.exam_type;
          if (a.question_number !== b.question_number) return a.question_number - b.question_number;
          return a.question_type.localeCompare(b.question_type);
        });

        setQuestionsInYear(combined);
        setYearQuestions((prev) => ({ ...prev, [selectedYear]: combined }));

        if (currentQuestionNumber !== null) {
          const existingQuestion =
            combined.find((item) => {
              if (currentExamType !== null && item.exam_type !== currentExamType) {
                return false;
              }
              if (currentQuestionType && item.question_type !== currentQuestionType) {
                return false;
              }
              return item.question_number === currentQuestionNumber;
            }) ??
            combined.find((item) => item.question_number === currentQuestionNumber) ??
            null;
          setQuestion(existingQuestion);
          if (!existingQuestion) {
            setCurrentQuestionNumber(null);
            setCurrentExamType(null);
            setCurrentQuestionType(null);
            navigate('/library', { replace: true });
          }
        } else {
          setQuestion(null);
        }
      } catch (error) {
        console.error('Failed to load questions:', error);
        setQuestionsError('Unable to load questions for this year.');
        setQuestionsInYear([]);
        setQuestion(null);
        setCurrentQuestionNumber(null);
        setCurrentExamType(null);
        setCurrentQuestionType(null);
        navigate('/library', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    loadQuestionsForYear();
  }, [selectedYear, currentQuestionNumber, currentExamType, currentQuestionType, navigate, yearQuestions]);

  useEffect(() => {
    if (question && selectedYear && currentQuestionNumber !== null) {
      navigate(`/library/${selectedYear}/${currentQuestionNumber}`, { replace: true });
    }
  }, [question, selectedYear, currentQuestionNumber, navigate]);

  useEffect(() => {
    setAnswerRevealed(false);
    setSelectedAnswer('');
  }, [question]);

  const filteredQuestions = useMemo(() => {
    if (!questionsInYear.length) {
      return [];
    }
    if (examFilter === 'exam1') {
      return questionsInYear.filter((item) => item.exam_type === 1);
    }
    if (examFilter === 'exam2-mc') {
      return questionsInYear.filter(
        (item) => item.exam_type === 2 && item.question_type === 'MC'
      );
    }
    if (examFilter === 'exam2-er') {
      return questionsInYear.filter(
        (item) => item.exam_type === 2 && item.question_type === 'ER'
      );
    }
    return questionsInYear;
  }, [examFilter, questionsInYear]);

  useEffect(() => {
    if (!question) {
      return;
    }
    const stillVisible = filteredQuestions.some(
      (item) => buildQuestionKey(item) === buildQuestionKey(question)
    );
    if (!stillVisible) {
      setQuestion(null);
      setCurrentQuestionNumber(null);
      setCurrentExamType(null);
      setCurrentQuestionType(null);
      setAnswerRevealed(false);
      setSelectedAnswer('');
      navigate('/library', { replace: true });
    }
  }, [examFilter, filteredQuestions, navigate, question]);

  const handleQuestionSelect = (year: number, selectedQuestion: LibraryQuestion) => {
    setSelectedYear(year);
    setCurrentQuestionNumber(selectedQuestion.question_number);
    setCurrentExamType(selectedQuestion.exam_type);
    setCurrentQuestionType(selectedQuestion.question_type);
    setQuestion(selectedQuestion);
    setAnswerRevealed(false);
    setSelectedAnswer('');
    setQuestionsError(null);
    navigate(`/library/${year}/${selectedQuestion.question_number}`);
  };
  const handleYearSelect = (year: number | null) => {
    if (!year) {
      setSelectedYear(null);
      setCurrentQuestionNumber(null);
      setCurrentExamType(null);
      setCurrentQuestionType(null);
      setQuestion(null);
      setQuestionsInYear([]);
      setQuestionsError(null);
      setAnswerRevealed(false);
      setSelectedAnswer('');
      navigate('/library', { replace: true });
      return;
    }

    if (year === selectedYear) {
      return;
    }

    setSelectedYear(year);
    setCurrentQuestionNumber(null);
    setCurrentExamType(null);
    setCurrentQuestionType(null);
    setQuestion(null);
    setQuestionsInYear([]);
    setQuestionsError(null);
    setAnswerRevealed(false);
    setSelectedAnswer('');
    navigate('/library', { replace: true });
  };

  const handleQuestionOptionSelect = (selectedQuestion: LibraryQuestion | null) => {
    if (!selectedYear || !selectedQuestion) {
      setCurrentQuestionNumber(null);
      setCurrentExamType(null);
      setCurrentQuestionType(null);
      setQuestion(null);
      setAnswerRevealed(false);
      setSelectedAnswer('');
      navigate('/library', { replace: true });
      return;
    }

    handleQuestionSelect(selectedYear, selectedQuestion);
  };

  const handleAnswerSelect = (answer: string) => {
    if (!answerRevealed) {
      setSelectedAnswer(answer);
    }
  };

  const revealAnswer = () => {
    setAnswerRevealed(true);
  };

  const currentIndex = useMemo(() => {
    if (!question) return -1;
    return filteredQuestions.findIndex((item) => buildQuestionKey(item) === buildQuestionKey(question));
  }, [question, filteredQuestions]);

  const goToPrevious = () => {
    if (currentIndex <= 0) return;
    const prevQuestion = filteredQuestions[currentIndex - 1];
    handleQuestionSelect(selectedYear!, prevQuestion);
  };

  const goToNext = () => {
    if (currentIndex < 0 || currentIndex >= filteredQuestions.length - 1) return;
    const nextQuestion = filteredQuestions[currentIndex + 1];
    handleQuestionSelect(selectedYear!, nextQuestion);
  };

  const status = getQuestionStatus(question);
  const totalQuestions = filteredQuestions.length;
  const isQuestionSelectDisabled =
    !selectedYear || isLoading || filteredQuestions.length === 0 || !!questionsError;
  const selectedQuestionStatus = question ? getQuestionStatus(question) : null;
  const selectedQuestionTypeLabel = question
    ? getQuestionTypeLabel(question.question_type)
    : null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-subject-600 dark:hover:text-subject-400"
            >
              <Home className="h-5 w-5 mr-2" />
              Dashboard
            </button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Question Library</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
          <aside className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 h-fit space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Browse Questions</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Choose a year and question to jump straight to it.
              </p>
            </div>
            {yearsLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-subject-500"></div>
              </div>
            ) : availableYears.length === 0 ? (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                No exam years available yet.
              </div>
            ) : (
              <div className="space-y-5">
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Year</span>
                  <Listbox value={selectedYear} onChange={handleYearSelect}>
                    {({ open }) => (
                      <div className="relative mt-1">
                        <Listbox.Button
                          className="relative w-full cursor-pointer rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-3 pl-4 pr-10 text-left text-sm text-gray-900 dark:text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-subject-500"
                        >
                          <span className="block">
                            {selectedYear ? (
                              <span className="font-semibold">{selectedYear}</span>
                            ) : (
                              <span className="text-gray-500 dark:text-gray-300">Select a year</span>
                            )}
                          </span>
                          <span className="block text-xs text-gray-500 dark:text-gray-400">
                            VCE Exam
                          </span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
                            <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
                          </span>
                        </Listbox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options className="absolute z-10 mt-2 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-2 shadow-lg focus:outline-none">
                            {availableYears.map((year) => (
                              <Listbox.Option
                                key={year}
                                value={year}
                                className={({ active }) =>
                                  `cursor-pointer select-none px-4 py-3 text-sm ${
                                    active ? 'bg-gray-100 dark:bg-gray-700/60' : ''
                                  }`
                                }
                              >
                                {({ selected }) => (
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <div className="font-semibold text-gray-900 dark:text-white">{year}</div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">VCE Exam</div>
                                    </div>
                                    {selected && (
                                      <Check className="h-4 w-4 text-subject-500" aria-hidden="true" />
                                    )}
                                  </div>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    )}
                  </Listbox>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Exam</span>
                  <Listbox value={examFilter} onChange={setExamFilter}>
                    {({ open }) => {
                      const selectedOption = EXAM_FILTER_OPTIONS.find(
                        (option) => option.value === examFilter
                      );
                      return (
                        <div className="relative mt-1">
                          <Listbox.Button
                            className="relative w-full cursor-pointer rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-3 pl-4 pr-10 text-left text-sm text-gray-900 dark:text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-subject-500"
                          >
                            <span className="block font-semibold">
                              {selectedOption?.label ?? 'All Exams'}
                            </span>
                            <span className="block text-xs text-gray-500 dark:text-gray-400">
                              {selectedOption?.description ?? 'Exam 1 + Exam 2'}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
                              <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
                            </span>
                          </Listbox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <Listbox.Options className="absolute z-10 mt-2 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-2 shadow-lg focus:outline-none">
                              {EXAM_FILTER_OPTIONS.map((option) => (
                                <Listbox.Option
                                  key={option.value}
                                  value={option.value}
                                  className={({ active }) =>
                                    `cursor-pointer select-none px-4 py-3 text-sm ${
                                      active ? 'bg-gray-100 dark:bg-gray-700/60' : ''
                                    }`
                                  }
                                >
                                  {({ selected }) => (
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <div className="font-semibold text-gray-900 dark:text-white">{option.label}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">{option.description}</div>
                                      </div>
                                      {selected && (
                                        <Check className="h-4 w-4 text-subject-500" aria-hidden="true" />
                                      )}
                                    </div>
                                  )}
                                </Listbox.Option>
                              ))}
                            </Listbox.Options>
                          </Transition>
                        </div>
                      );
                    }}
                  </Listbox>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Question</span>
                  <Listbox
                    value={question ?? null}
                    onChange={handleQuestionOptionSelect}
                    disabled={isQuestionSelectDisabled}
                  >
                    {({ open }) => (
                      <div className="relative mt-1">
                        <Listbox.Button
                          disabled={isQuestionSelectDisabled}
                          className={`relative w-full rounded-lg border py-3 pl-4 pr-10 text-left text-sm shadow-sm transition focus:outline-none focus:ring-2 focus:ring-subject-500 ${
                            isQuestionSelectDisabled
                              ? 'cursor-not-allowed border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700/60 text-gray-500 dark:text-gray-400'
                              : 'cursor-pointer border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                          }`}
                        >
                          {question ? (
                            <div>
                              <div className="font-semibold text-gray-900 dark:text-white">
                                Question {question.question_number} · Exam {question.exam_type}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-300">
                                {selectedQuestionTypeLabel}
                                {selectedQuestionStatus && (
                                  <span
                                    className={`inline-flex items-center rounded-full px-2 py-0.5 font-semibold ${selectedQuestionStatus.className}`}
                                  >
                                    {selectedQuestionStatus.label}
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="text-gray-500 dark:text-gray-300">
                              {questionsError
                                ? 'Unable to load questions'
                                : !selectedYear
                                ? 'Select a year first'
                                : isLoading
                                ? 'Loading questions...'
                                : filteredQuestions.length === 0
                                ? 'No questions available for this exam filter'
                                : 'Select a question'}
                            </div>
                          )}
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
                            <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
                          </span>
                        </Listbox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options className="absolute z-10 mt-2 max-h-72 w-full overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-2 shadow-lg focus:outline-none">
                            {filteredQuestions.map((item) => {
                              const status = getQuestionStatus(item);
                              const typeLabel = getQuestionTypeLabel(item.question_type);
                              return (
                                <Listbox.Option
                                  key={buildQuestionKey(item)}
                                  value={item}
                                  className={({ active }) =>
                                    `cursor-pointer select-none px-4 py-3 text-sm ${
                                      active ? 'bg-gray-100 dark:bg-gray-700/60' : ''
                                    }`
                                  }
                                >
                                  {({ selected }) => (
                                    <div className="flex items-start justify-between gap-3">
                                      <div>
                                        <div className="font-semibold text-gray-900 dark:text-white">
                                          Question {item.question_number} · Exam {item.exam_type}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-300">
                                          {typeLabel}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span
                                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${status.className}`}
                                        >
                                          {status.label}
                                        </span>
                                        {selected && (
                                          <Check className="h-4 w-4 text-subject-500" aria-hidden="true" />
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </Listbox.Option>
                              );
                            })}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    )}
                  </Listbox>
                </div>
                {isLoading && selectedYear && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-subject-500"></div>
                    Loading questions...
                  </div>
                )}
                {questionsError && (
                  <div className="text-sm text-red-600 dark:text-red-400">{questionsError}</div>
                )}
              </div>
            )}
          </aside>

          <main className="space-y-6">
            {!selectedYear ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-10 text-center space-y-3">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Browse Past Exam Questions</h2>
                <p className="text-lg text-gray-600 dark:text-gray-400">
                  Pick a year from the menu to see its questions.
                </p>
              </div>
            ) : question ? (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {selectedYear} Exam {question.exam_type} · Question {question.question_number}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {`${getQuestionTypeLabel(question.question_type)} Question`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={goToPrevious}
                      disabled={currentIndex <= 0}
                      className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm disabled:opacity-40"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                      onClick={goToNext}
                      disabled={currentIndex < 0 || currentIndex >= totalQuestions - 1}
                      className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm disabled:opacity-40"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${status.className}`}>
                    {status.label}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {currentIndex >= 0 ? `Question ${currentIndex + 1} of ${totalQuestions}` : ''}
                  </span>
                </div>

                {isShortAnswerQuestionGroup(question) ? (
                  <ShortAnswerLibraryCard
                    key={question.id}
                    question={question}
                    canGenerateSimilar={!!subscriptionStatus?.has_active_subscription}
                  />
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-5">
                    <div className="text-gray-900 dark:text-white text-lg">
                      <MathRenderer latex={question.question_text} />
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Difficulty:{' '}
                      <span className={getDifficultyColor(question.difficulty_rating)}>
                        {question.difficulty_rating ?? 'N/A'}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {['A', 'B', 'C', 'D', 'E'].map((key) => {
                        const keyLower = key.toLowerCase() as 'a' | 'b' | 'c' | 'd' | 'e';
                        const optionText = (question as QuestionApiModel)[`option_${keyLower}`];
                        if (!optionText) return null;
                        const isCorrect = question.correct_answer === key;
                        const isSelected = selectedAnswer === key;
                        let className = 'border-gray-300 dark:border-gray-600';
                        if (answerRevealed) {
                          if (isCorrect) {
                            className = 'border-green-500 bg-green-50 dark:bg-green-900/20';
                          } else if (isSelected) {
                            className = 'border-red-500 bg-red-50 dark:bg-red-900/20';
                          }
                        } else if (isSelected) {
                          className = 'border-subject-500 bg-subject-50 dark:bg-subject-900/20';
                        }
                        const isInactive = answerRevealed;
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => handleAnswerSelect(key)}
                            aria-disabled={isInactive}
                            tabIndex={isInactive ? -1 : 0}
                            className={`w-full p-4 border-2 rounded-lg text-left transition-all ${className}`}
                          >
                            <div className="flex items-start gap-3">
                              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-sm font-medium">
                                {key}
                              </span>
                              <div className="flex-1 text-gray-900 dark:text-white">
                                <MathRenderer latex={optionText} />
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {!answerRevealed && (
                      <button
                        onClick={revealAnswer}
                        className="inline-flex items-center px-4 py-2 bg-subject-600 text-white rounded-lg hover:bg-subject-500"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Reveal Answer
                      </button>
                    )}
                    {answerRevealed && (
                      <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          Correct Answer: {question.correct_answer}
                        </span>
                      </div>
                    )}
                    {question.worked_solution ? (
                      <WorkedSolutionPanel solution={question.worked_solution} isVisible={answerRevealed} />
                    ) : (
                      <div className="px-4 py-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg text-sm text-yellow-800 dark:text-yellow-200">
                        Worked solution not available yet.
                      </div>
                    )}

                    <SimilarQuestionPanel
                      questions={[{ id: question.id, label: 'Question' }]}
                      canGenerate={!!subscriptionStatus?.has_active_subscription}
                    />
                  </div>
                )}
              </div>
            ) : isLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-subject-500"></div>
              </div>
            ) : questionsError ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-10 text-center text-red-600 dark:text-red-400">
                {questionsError}
              </div>
            ) : filteredQuestions.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-10 text-center text-gray-600 dark:text-gray-400">
                No questions available for {selectedYear} with this exam filter.
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-10 text-center text-gray-600 dark:text-gray-400">
                Select a question from the menu to view it.
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default QuestionLibrary;
